<template>
  <ResponsiveDialog v-model="visible" width="480px">
    <template #header>
      <div class="nsheet__head">
        <span class="nsheet__title">알림</span>
        <!-- 미확인 수. 목록은 20건씩 끊어 오지만 이 숫자는 서버 COUNT 라
             화면에 보이는 행 수보다 클 수 있다 — 그래서 99+ 로 접는다. -->
        <span
          v-if="unread > 0"
          class="count-badge count-badge--primary"
          role="status"
          :aria-label="`읽지 않은 알림 ${unread}건`"
        >
          {{ unread > 99 ? '99+' : unread }}
        </span>
        <button
          v-if="unread > 0"
          type="button"
          class="nsheet__readall"
          @click="onReadAll"
        >모두 읽음</button>
      </div>
    </template>

    <div class="nsheet__body">
      <div v-if="loading && !items.length" class="nsheet__skel">
        <div v-for="i in 4" :key="i" class="skel-row nsheet__skel-row" />
      </div>

      <div v-else-if="!items.length" class="empty-state">
        <div class="empty-state__icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9a6 6 0 1 1 12 0c0 4 1.5 5.5 1.5 5.5h-15S6 13 6 9z"/><path d="M10 18.5a2.2 2.2 0 0 0 4 0"/></svg></div>
        <div class="empty-state__title">새로운 알림이 없습니다</div>
        <div class="empty-state__hint">결재 요청·업무 배정·댓글이 여기로 옵니다</div>
      </div>

      <template v-else>
        <section v-for="g in groups" :key="g.key" class="nsheet__group">
          <h4 class="nsheet__glabel">{{ g.label }}</h4>
          <button
            v-for="n in g.items"
            :key="n.id"
            type="button"
            class="nrow"
            :class="[`t-${kindOf(n).tone}`, { 'is-unread': !n.readAt }]"
            @click="open(n)"
          >
            <span class="nrow__dot" :class="{ 'is-on': !n.readAt }" />

            <span class="nrow__ava">
              <el-avatar v-if="n.actor" :size="32" :src="avatarOf(n.actor)">
                {{ initialOf(n.actor) }}
              </el-avatar>
              <span v-else class="nrow__sys">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path v-for="(d, i) in kindOf(n).paths" :key="i" :d="d" />
                </svg>
              </span>
              <!-- 아바타가 있는 행에도 종류 표시가 필요하다. 사람 얼굴만으로는
                   "배정인지 반려인지" 를 제목을 읽기 전에는 알 수 없다. -->
              <span v-if="n.actor" class="nrow__kind">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path v-for="(d, i) in kindOf(n).paths" :key="i" :d="d" />
                </svg>
              </span>
            </span>

            <span class="nrow__body">
              <span class="nrow__title">{{ n.title }}</span>
              <span v-if="n.body" class="nrow__text u-clamp-2">{{ n.body }}</span>
              <!-- u-num(모노)을 붙이지 않는다. relTime 은 '방금 · 3분 전' 처럼
                   한글이 섞이는데 --font-mono 에는 한글 글리프가 없어 숫자만
                   모노로 튀는 혼합 조판이 된다. u-num 은 08:30 · 08-21 · 건수용이다. -->
              <span class="nrow__when">{{ relTime(n.createdAt) }}<template v-if="n.gone"> · 삭제된 항목</template></span>
            </span>

            <span v-if="!n.gone" class="nrow__chev">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="m9.5 5.5 6.5 6.5-6.5 6.5"/>
              </svg>
            </span>
          </button>
        </section>

        <!--
          연타 방지. cursor 는 응답이 와야 갱신되므로, 로딩 중에 한 번 더 누르면
          같은 before= 로 두 번 요청이 나가고 같은 행이 목록에 두 번 붙는다
          (:key 가 n.id 라 Vue 가 중복 키 경고까지 낸다).
        -->
        <button
          v-if="cursor"
          type="button"
          class="nsheet__more"
          :disabled="loading"
          @click="loadMore"
        >{{ loading ? '불러오는 중…' : '더 보기' }}</button>
      </template>
    </div>
  </ResponsiveDialog>
</template>

<script>
import { computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import ResponsiveDialog from '@/components/dialogs/ResponsiveDialog.vue'
import { getProfileImageUrl, getProfileInitial } from '@/utils/profileUtils'

/**
 * 알림함.
 *
 * 목록은 시트를 열 때만 가져온다. 폴링은 /count 만 치므로(COUNT 한 번)
 * 60초마다 목록을 통째로 끌어오는 낭비가 없다.
 */
const DAY = 86400000

/**
 * 알림 종류 → 색조 + 아이콘.
 *
 * 세로로 훑을 때 제목을 읽기 전에 "무엇에 관한 알림인지" 를 알려주는 장치다.
 * 색 의미는 앱 전체 규칙과 같다 — 앰버=대기(내 결재 차례), 초록=승인·완료,
 * 빨강=반려, 파랑=업무, 회색=댓글. 결재 알림을 전부 앰버로 칠하면
 * "반려됐습니다" 와 "승인됐습니다" 가 같은 색이 되어 신호가 죽는다.
 *
 * Notification.type 은 ENUM 이 아니라 STRING 이라(마이그레이션 없이 늘어난다)
 * 모르는 값이 반드시 들어온다. 그때는 subjectKind(task|report) 로 떨어지고
 * 그것도 없으면 회색 종으로 떨어진다 — 아이콘 자리가 비어 정렬이 깨지는 일이 없게.
 */
const ICON = {
  bell: ['M6 9a6 6 0 1 1 12 0c0 4 1.5 5.5 1.5 5.5h-15S6 13 6 9z', 'M10 18.5a2.2 2.2 0 0 0 4 0'],
  task: ['M8 5H6.6A1.6 1.6 0 0 0 5 6.6v12.8A1.6 1.6 0 0 0 6.6 21h10.8a1.6 1.6 0 0 0 1.6-1.6V6.6A1.6 1.6 0 0 0 17.4 5H16', 'M8.9 3h6.2a.9.9 0 0 1 .9.9V6H8V3.9a.9.9 0 0 1 .9-.9z'],
  comment: ['M20.5 12.2a7.6 7.6 0 0 1-11 6.8L5 20.5l1.5-4.4a7.6 7.6 0 1 1 14-3.9z'],
  wait: ['M12 3.6a8.4 8.4 0 1 0 0 16.8 8.4 8.4 0 0 0 0-16.8z', 'M12 7.6V12l3.1 1.9'],
  ok: ['M12 3.6a8.4 8.4 0 1 0 0 16.8 8.4 8.4 0 0 0 0-16.8z', 'M8.3 12.2l2.5 2.5 4.9-5.1'],
  no: ['M12 3.6a8.4 8.4 0 1 0 0 16.8 8.4 8.4 0 0 0 0-16.8z', 'M9.4 9.4l5.2 5.2M14.6 9.4l-5.2 5.2'],
  // 상태 이동(화살표) · 내용 수정(연필)
  move: ['M4 12h13', 'M13 7.5 17.8 12 13 16.5'],
  edit: ['M4 20h4L19.2 8.8a2 2 0 0 0 0-2.8l-1.2-1.2a2 2 0 0 0-2.8 0L4 16v4z'],
  // 영업 방문(지도 핀)
  pin: ['M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 1 1 13 0c0 5.4-6.5 11-6.5 11z', 'M12 12.4a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8z']
}

const KIND_BY_TYPE = {
  task_assigned: { tone: 'primary', paths: ICON.task },
  task_reassigned: { tone: 'primary', paths: ICON.task },
  task_claimed: { tone: 'primary', paths: ICON.task },
  task_supporter_added: { tone: 'primary', paths: ICON.task },
  task_comment_added: { tone: 'muted', paths: ICON.comment },
  // 상태 변경은 파랑(업무) — 초록으로 두면 '완료' 로 읽혀서 '대기로 되돌림' 도 초록이 된다.
  task_status_changed: { tone: 'primary', paths: ICON.move },
  task_updated: { tone: 'muted', paths: ICON.edit },
  task_completion_requested: { tone: 'warning', paths: ICON.wait },
  task_completion_approved: { tone: 'success', paths: ICON.ok },
  task_completion_rejected: { tone: 'danger', paths: ICON.no },
  task_force_completed: { tone: 'success', paths: ICON.ok },
  task_completion_canceled: { tone: 'muted', paths: ICON.move },
  report_submitted: { tone: 'warning', paths: ICON.wait },
  report_turn: { tone: 'warning', paths: ICON.wait },
  report_approved: { tone: 'success', paths: ICON.ok },
  report_rejected: { tone: 'danger', paths: ICON.no },
  // 영업 방문 — 외근과 같은 '현장' 의미라 파랑(업무)으로 두고 아이콘으로 구분한다
  sales_visit_assigned: { tone: 'primary', paths: ICON.pin },
  sales_visit_changed: { tone: 'primary', paths: ICON.pin },
  sales_visit_canceled: { tone: 'muted', paths: ICON.pin }
}

const KIND_BY_SUBJECT = {
  task: { tone: 'primary', paths: ICON.task },
  report: { tone: 'warning', paths: ICON.wait },
  comment: { tone: 'muted', paths: ICON.comment },
  sales_visit: { tone: 'primary', paths: ICON.pin }
}

const KIND_FALLBACK = { tone: 'muted', paths: ICON.bell }

export default {
  name: 'NotificationSheet',
  components: { ResponsiveDialog },
  props: {
    modelValue: { type: Boolean, default: false }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const store = useStore()
    const router = useRouter()

    const visible = computed({
      get: () => props.modelValue,
      set: (v) => emit('update:modelValue', v)
    })

    const items = computed(() => store.state.notifications)
    const unread = computed(() => store.state.notificationUnread)
    const cursor = computed(() => store.state.notificationCursor)
    const loading = computed(() => store.state.notificationLoading)

    function startOfToday() {
      const d = new Date()
      d.setHours(0, 0, 0, 0)
      return d.getTime()
    }

    /*
     * 오늘 / 어제 / 이전.
     *
     * 예전 가운데 칸은 '이번 주' 라고 적혀 있었지만 실제 경계는 `today - 6*DAY`,
     * 즉 **최근 7일**이었다. 화요일에 열면 지난주 목·금이 '이번 주' 에 들어간다.
     * 라벨이 거짓말을 하느니 경계를 라벨에 맞춘다 — '어제' 는 경계가 하나뿐이라
     * 어긋날 수 없고, 알림은 대부분 최근 것이라 이 분할로 충분하다.
     */
    const groups = computed(() => {
      const today = startOfToday()
      const yesterday = today - DAY
      const buckets = { today: [], yesterday: [], older: [] }
      for (const n of items.value) {
        const t = new Date(String(n.createdAt).replace(' ', 'T')).getTime()
        if (t >= today) buckets.today.push(n)
        else if (t >= yesterday) buckets.yesterday.push(n)
        else buckets.older.push(n)
      }
      return [
        { key: 'today', label: '오늘', items: buckets.today },
        { key: 'yesterday', label: '어제', items: buckets.yesterday },
        { key: 'older', label: '이전', items: buckets.older }
      ].filter((g) => g.items.length)
    })

    function relTime(v) {
      if (!v) return ''
      const t = new Date(String(v).replace(' ', 'T')).getTime()
      const diff = Date.now() - t
      const min = Math.floor(diff / 60000)
      if (min < 1) return '방금'
      if (min < 60) return `${min}분 전`
      const hr = Math.floor(min / 60)
      if (hr < 24) return `${hr}시간 전`
      const day = Math.floor(hr / 24)
      if (day < 7) return `${day}일 전`
      return new Date(t).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })
    }

    /** 표시 전용 — 알림 한 건을 색조/아이콘으로 옮긴다 */
    function kindOf(n) {
      return KIND_BY_TYPE[n.type] || KIND_BY_SUBJECT[n.subjectKind] || KIND_FALLBACK
    }

    async function open(n) {
      if (!n.readAt) store.dispatch('markNotificationRead', n.id)

      // 대상(업무 · 보고 · 방문)이 지워졌다(서버가 gone 으로 알려 준다). 이동하면 '찾을 수 없음' 화면만 열리므로
      // 시트에 머물러 안내만 한다 — 읽음 처리는 위에서 했다.
      if (n.gone) {
        ElMessage.info('삭제된 항목이라 열 수 없습니다.')
        return
      }

      // 시트를 닫고 이동하는 게 아니라 **이동으로 닫는다**.
      // 시트가 URL(?sheet=notif)에 붙어 있어서 visible=false 는 router.back() 을
      // 부른다. 그 직후 push 를 하면 뒤로가기와 이동이 겹쳐 엉뚱한 화면에 안착한다.
      // replace 를 쓰면 ?sheet=notif 항목이 소비되므로, 목적지에서 뒤로가기를
      // 눌렀을 때 알림함이 다시 열리지 않고 원래 보던 화면으로 돌아간다.
      if (n.linkPath) router.replace(n.linkPath).catch(() => {})
      else visible.value = false
    }

    function onReadAll() {
      store.dispatch('markAllNotificationsRead')
    }

    function loadMore() {
      store.dispatch('fetchNotifications', { append: true }).catch(() => {})
    }

    // 열 때만 목록을 가져온다
    watch(visible, (v) => {
      if (v) store.dispatch('fetchNotifications').catch(() => {})
    })

    return {
      visible, items, unread, cursor, loading, groups,
      relTime, kindOf, open, onReadAll, loadMore,
      avatarOf: (u) => getProfileImageUrl(u),
      initialOf: (u) => getProfileInitial(u)
    }
  }
}
</script>

<style scoped>
/* ---------------------------------------------------------------
   색조. 한 행이 쓰는 세 가지 색(진한 면 / 옅은 면 / 옅은 면 위 글자)을
   행 루트에 변수로 얹고, 아래 규칙들은 --nk-* 만 읽는다.
   종류별 셀렉터를 아이콘·배지마다 다섯 벌씩 쓰지 않으려는 것.
   --------------------------------------------------------------- */
.t-primary { --nk: var(--color-primary); --nk-soft: var(--color-primary-soft); --nk-fg: var(--color-primary-strong); }
.t-warning { --nk: var(--color-warning); --nk-soft: var(--color-warning-soft); --nk-fg: var(--color-warning-strong); }
.t-success { --nk: var(--color-success); --nk-soft: var(--color-success-soft); --nk-fg: var(--color-success-strong); }
.t-danger  { --nk: var(--color-danger);  --nk-soft: var(--color-danger-soft);  --nk-fg: var(--color-danger-strong); }
.t-muted   { --nk: var(--color-text-subtle); --nk-soft: var(--color-surface-2); --nk-fg: var(--color-text-muted); }

/* ---------------------------------------------------------------
   헤더
   --------------------------------------------------------------- */
.nsheet__head { display: flex; align-items: center; gap: var(--sp-2); flex: 1; min-width: 0; }
.nsheet__title { font-size: var(--fs-lg); font-weight: var(--fw-semibold); color: var(--color-text); }

/*
 * 시각 높이는 30px 로 두고 히트 영역만 --tap-min 으로 넓힌다(::after).
 * min-height:44 를 그대로 주면 모바일 시트 헤더가 닫기 버튼(36px)보다 높아져
 * 헤더 줄 전체가 8px 두꺼워진다. ResponsiveDialog 의 .rd-close 와 같은 수법.
 */
.nsheet__readall {
  position: relative;
  margin-left: auto;
  height: 30px;
  padding: 0 var(--sp-2);
  border: 0;
  border-radius: var(--r-sm);
  background: none;
  color: var(--color-primary);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  cursor: pointer;
  flex-shrink: 0;
}
.nsheet__readall::after {
  content: '';
  position: absolute;
  inset: -7px 0;
}
.nsheet__readall:active { background: var(--color-primary-soft); }

.nsheet__body { min-height: 160px; }

/* ---------------------------------------------------------------
   그룹
   --------------------------------------------------------------- */
.nsheet__group { margin-bottom: var(--sp-2); }

/*
 * 스티키 라벨. 목록이 길어지면 지금 보고 있는 묶음이 오늘인지 이전인지
 * 스크롤 중에 사라진다. 스크롤 컨테이너는 ResponsiveDialog 의 .rd-body 이고,
 * 라벨이 행 위로 지나갈 때 배경이 비치면 안 되므로 surface 를 깔아 둔다.
 * 좌우 여백을 음수로 빼는 이유는 아래 .nrow 와 같다 — 라벨 배경이 행의
 * 강조 배경과 같은 폭이어야 스크롤 중에 계단이 지지 않는다.
 */
.nsheet__glabel {
  position: sticky;
  top: 0;
  z-index: 1;
  margin: 0 calc(-1 * var(--sp-2));
  padding: var(--sp-2) var(--sp-2) var(--sp-1);
  background: var(--color-surface);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: 0.06em;
  color: var(--color-text-muted);
}

/* ---------------------------------------------------------------
   행
   --------------------------------------------------------------- */
/*
 * 폭을 좌우 8px 씩 넘겨 잡는다(width calc + 음수 margin). 미확인 행의 배경이
 * 시트 본문 여백까지 살짝 물어야 "행 전체가 강조된" 것으로 읽히고,
 * 글자 시작 위치는 그대로 유지된다. box-sizing 은 base.css 에서 border-box 다.
 */
.nrow {
  position: relative;
  width: calc(100% + var(--sp-4));
  margin: 0 calc(-1 * var(--sp-2));
  display: flex;
  align-items: flex-start;
  gap: var(--sp-2);
  padding: var(--sp-3) var(--sp-2);
  border: 0;
  border-bottom: 1px solid var(--color-border);
  background: none;
  text-align: left;
  cursor: pointer;
  min-height: var(--tap-comfortable);
  transition: background var(--dur-fast) var(--ease);
}
.nrow:last-child { border-bottom: 0; }

/*
 * 미확인 강조. --color-primary-soft 원색은 흰 시트 위에서 한 화면에 대여섯 줄이
 * 겹치면 파란 블록처럼 보인다. surface 와 섞어 한 단계 눌러 쓴다.
 * color-mix 를 모르는 브라우저는 앞줄(원색)로 떨어진다 — 강조가 조금 진해질 뿐
 * 신호는 살아 있다.
 */
.nrow.is-unread {
  background: var(--color-primary-soft);
  background: color-mix(in srgb, var(--color-primary-soft) 45%, var(--color-surface));
}

/*
 * hover / active 는 반드시 .nrow.is-unread **뒤**에 온다.
 * .nrow:active 와 .nrow.is-unread 는 특정도가 (0,2,0) 으로 같아서, 앞에 두면
 * 나중에 선언된 미확인 배경이 이겨 버린다 — 정작 눌러야 할 미확인 행만
 * 탭 피드백이 없는 상태가 된다.
 */
.nrow:hover { background: var(--color-surface-2); }
.nrow:active { background: var(--color-border); }
.nrow.is-unread:hover { background: var(--color-primary-soft); }
.nrow.is-unread:active { background: var(--color-primary-soft-press); }

/* 7px 점. 아바타(32) 세로 중앙에 맞춘다 — (32 - 7) / 2 ≈ 12 */
.nrow__dot {
  width: 7px;
  height: 7px;
  border-radius: var(--r-full);
  background: transparent;
  flex-shrink: 0;
  margin-top: 12px;
}
.nrow__dot.is-on { background: var(--color-primary); }

.nrow__ava { position: relative; width: 32px; height: 32px; flex-shrink: 0; }
.nrow__ava :deep(.el-avatar) {
  background: var(--color-surface-2);
  color: var(--color-text-muted);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
}

/* 시스템 알림(작성자가 없는 알림)의 아이콘 자리. 아바타와 같은 32px 원이라
   목록에서 좌측 정렬이 흐트러지지 않는다. */
.nrow__sys {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--r-full);
  background: var(--nk-soft);
  color: var(--nk-fg);
}
.nrow__sys svg { width: 17px; height: 17px; }

/* 아바타 우하단의 종류 배지. surface 링을 둘러 아바타 사진 위에서도 경계가 보인다. */
.nrow__kind {
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 15px;
  height: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--r-full);
  background: var(--nk);
  color: var(--color-surface);
  box-shadow: 0 0 0 2px var(--color-surface);
}
.nrow__kind svg { width: 9px; height: 9px; }

.nrow__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }

.nrow__title {
  font-size: var(--fs-md);
  font-weight: var(--fw-medium);
  color: var(--color-text);
  line-height: 1.45;
  overflow-wrap: anywhere;
}
.is-unread .nrow__title { font-weight: var(--fw-semibold); }

.nrow__text {
  font-size: var(--fs-sm);
  line-height: 1.45;
  color: var(--color-text-muted);
}

.nrow__when { font-size: var(--fs-xs); color: var(--color-text-subtle); margin-top: 1px; }

/* 이 행이 어딘가로 이동한다는 표시. 알림은 전부 linkPath 를 갖고 열리면
   해당 화면으로 replace 된다. 대상이 지워진 알림(gone)만 이동하지 않으므로 뺀다. */
.nrow__chev {
  align-self: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: var(--color-text-subtle);
}
.nrow__chev svg { width: 14px; height: 14px; display: block; }

/* ---------------------------------------------------------------
   더 보기 / 스켈레톤
   --------------------------------------------------------------- */
.nsheet__more {
  width: 100%;
  min-height: var(--tap-min);
  border: 1px solid var(--color-border);
  border-radius: var(--r-md);
  background: var(--color-surface);
  color: var(--color-text-muted);
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  cursor: pointer;
  margin-top: var(--sp-3);
}
.nsheet__more:hover:not(:disabled) { background: var(--color-surface-2); border-color: var(--color-border-strong); }
.nsheet__more:disabled { color: var(--color-text-subtle); cursor: default; }

/* 빈 상태는 primitives.css 의 .empty-state 를 쓴다.
   shimmer 도 .skel-row 로 옮겼다 — 세 화면이 각자 복제하고 있었다. */
.nsheet__skel { display: flex; flex-direction: column; gap: var(--sp-3); padding: var(--sp-2) 0; }
.nsheet__skel-row { height: 48px; }

/* 좌우 -8px 물림은 **모바일 시트 전용**이다. .rd-body--mobile 은 padding:16px 이라
   음수 마진이 그 안으로 들어가지만, 데스크톱 본문에는 좌우 패딩이 없어서
   .rd-body 의 overflow-x:hidden 이 잘라낸다 — 배경은 티가 안 나도
   :focus-visible 링(outline-offset 2px)의 세로변이 잘려 나간다. */
@media (min-width: 768px) { /* --bp-mobile-max + 1 */
  .nrow,
  .nsheet__glabel {
    width: 100%;
    margin-left: 0;
    margin-right: 0;
  }
}

@media (max-width: 767px) { /* --bp-mobile-max */
  /* 아바타와 글자 사이를 한 칸 넓힌다. 손가락으로 훑을 때 아바타 열과 제목 열이
     붙어 있으면 두 개가 한 덩어리로 보여 행 경계를 못 읽는다. */
  .nrow { gap: var(--sp-3); }
}
</style>
